<template lang="html">
  <div class="fsd-registration">
    <!-- eslint-disable vue/no-v-html -->
    <figure
      v-if="!registeredEmail && campaignLayer && campaignLayer.imageSrcset"
      class="registration-fence__image-container"
      v-html="campaignLayer.imageSrcset"
    />
    <!-- eslint-enable vue/no-v-html -->

    <div class="fsd-session__headline">
      {{ headline }}
    </div>

    <template v-if="!registeredEmail && campaignLayer">
      <!-- eslint-disable vue/no-v-html -->
      <div
        v-if="campaignLayer.text"
        class="registration-fence__info"
        v-html="campaignLayer.text.html_safe || campaignLayer.text"
      />
      <!-- eslint-enable vue/no-v-html -->
      <div v-else class="registration-fence__info">
        {{ 'client.registration.fence.info' | t }}
      </div>
    </template>

    <div
      v-if="
        !registeredEmail && searchProfileTags && searchProfileTags.length > 0
      "
      class="registration-fence__pills"
    >
      <span
        v-for="(tag, idx) in searchProfileTags"
        :key="`searchProfileTag${idx}`"
        class="registration-fence__pill"
      >
        {{ tag }}
      </span>
    </div>

    <div v-if="registrationFailed" class="form-error">
      <p>{{ `client.registration_modal.error.${login.failure.status}` | t }}</p>
    </div>

    <div
      v-if="registeredEmail"
      class="row align-center align-middle large-collapse"
    >
      <EmailSent type="registration" />
    </div>

    <div
      v-else
      class="fsd-registration__form row align-center align-middle large-collapse"
    >
      <template v-if="isOptInConsentHintPresent">
        <div class="fsd-registration__page row align-center">
          <form
            ref="form"
            v-tracking-context="{ event_element: 'email' }"
            class="columns small-10 medium-8 large-7"
            action=""
            @submit.prevent="submitRegistration"
          >
            <input
              v-model="email"
              type="text"
              name="email"
              :placeholder="'client.registration_modal.email' | t"
              class="input input--login input--validate"
              :class="{ 'input--invalid': registrationFailed }"
            />
            <div class="fsd-registration__sponsored-mailing-opt-in">
              <input
                id="sponsored-mailing-opt-in"
                class="account-details__terms-custom-input custom-input--checkbox custom-input"
                type="checkbox"
                @change="onChangeSponsoredMailingOptIn"
              />
              <!-- eslint-disable vue/no-v-html -->
              <label
                for="sponsored-mailing-opt-in"
                class="fsd-registration__sponsored-mailing-opt-in--text"
                v-html="sponsoredMailingOptInConsentHint.text"
              >
              </label>
              <!-- eslint-enable vue/no-v-html -->
            </div>

            <button
              ref="registration_button"
              v-click-track="{ action_type: 'register' }"
              class="button button--secondary expanded"
            >
              {{ 'client.registration.buttons.email' | t }}
            </button>
            <div
              v-if="jobMarket !== 'ZEIT_JOBS'"
              v-tracking-context="{ event_element: 'social' }"
              class="social-login"
            >
              <socialLogin
                :search-profile="searchProfile"
                :rc="registrationCampaignId"
              />
            </div>
          </form>
        </div>
        <div
          v-tracking-context="{ event_element: 'privacy_notice' }"
          class="fsd-registration__footer"
        >
          <!-- eslint-disable vue/no-v-html -->
          <p
            class="account-creation__notice fsd-registration-modal__notice"
            v-html="optInConsentHint.text"
          />
          <!-- eslint-enable vue/no-v-html -->
        </div>
      </template>

      <template v-else>
        <div
          class="fsd-registration__page row align-center align-middle large-collapse"
        >
          <div class="fsd-register__page columns small-10 medium-8 large-7">
            {{ 'client.registration_modal.error.missing_prerequisite' | t }}
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import _debounce from 'lodash-es/debounce'
import _get from 'lodash-es/get'
import Cookies from 'js-cookie'

import SocialLogin from '@/vue/components/fused/sessions/social_login'
import EmailSent from '@/vue/components/fused/sessions/email_sent'
import { manualTrack } from '@/vue/directives/tracking'
import { registerGoogleTagManagerEvent } from '@/gtm_tracking/events'

export default {
  components: { EmailSent, SocialLogin },
  props: {
    returnTo: {
      type: String,
      default: null
    },
    campaignLayer: {
      type: Object,
      default: null
    },
    registrationCampaignId: {
      type: String,
      default: null
    },
    searchProfile: {
      type: Object,
      default: () => ({
        tagLabels: null,
        name: null,
        query: null,
        code: null,
        isActive: null
      })
    },
    pageId: {
      type: Number,
      default: null
    }
  },
  data: () => ({
    email: ''
  }),
  computed: {
    ...mapState([
      'jobMarket',
      'links',
      'optInConsentHint',
      'sponsoredMailingOptInConsentHint',
      'hasSponsoredMailingOptIn',
      'login',
      'initialIntent',
      'initialIntentData'
    ]),
    headline() {
      if (this.registeredEmail) {
        return this.$options.filters.t('client.email_sent.headline')
      } else if (this.campaignLayer && this.campaignLayer.title) {
        return this.campaignLayer.title
      } else if (this.campaignLayer && this.campaignLayer.headlineTranslation) {
        return this.$options.filters.t(
          `client.registration.fence.${this.campaignLayer.headlineTranslation}`
        )
      } else {
        return this.$options.filters.t('client.registration.headline')
      }
    },
    registrationFailed() {
      return this.login.failure && this.login.failure.type === 'registration'
    },
    registeredEmail() {
      return this.login.registrationEmail
    },
    isOptInConsentHintPresent() {
      return !!this.optInConsentHint
    },
    query() {
      return (_get(this.searchProfile, 'customTags') || []).join('%2C')
    },
    queryParams() {
      return [
        `code=${this.code}`,
        `name=${this.name}`,
        `query=${this.query}`,
        `intent=${this.initialIntent || ''}`,
        `intent_data=${this.initialIntentData || ''}`
      ].join('&')
    },
    searchProfileTags() {
      return _get(this.searchProfile, 'tagLabels')
    }
  },
  beforeMount() {
    this.ensureOptInConsentHintPresence()
    this.ensureSponsoredMailingConsentHintPresence()
  },
  methods: {
    ...mapActions([
      'registerUser',
      'ensureOptInConsentHintPresence',
      'ensureSponsoredMailingConsentHintPresence',
      'generateSearchProfileCode',
      'setHasSponsoredMailingOptIn',
      'loginFailureReset',
      'loginFailureSet',
      'loginEmailSet'
    ]),
    onChangeSponsoredMailingOptIn: _debounce(
      function ({ target }) {
        this.setHasSponsoredMailingOptIn(target.checked)
      },
      500,
      { trailing: true, leading: false }
    ),
    submitRegistration() {
      this.loginFailureReset()

      this.registerUser({
        email: this.email,
        hasSponsoredMailingOptIn: this.hasSponsoredMailingOptIn,
        code: _get(this.searchProfile, 'code') || null,
        q: _get(this.searchProfile, 'q') || null,
        customTags: (_get(this.searchProfile, 'customTags') || []).join(','),
        categoryTags: _get(this.searchProfile, 'tagKeys') || [],
        isActive: _get(this.searchProfile, 'isActive', true),
        searchProfileName: _get(this.searchProfile, 'name'),
        campaignLayerName: _get(this.campaignLayer, 'name', null),
        registrationCampaign: this.registrationCampaignId,
        returnTo: this.returnTo,
        pageId: this.pageId
      })
        .then(({ user, webtrekk }) => {
          EventBus.trigger('tracking:registrationSuccess', {
            pixelsConfig: webtrekk
          })
          registerGoogleTagManagerEvent('registrationSuccess')
          this.loginEmailSet({ type: 'registration', email: user.email })

          Cookies.set('registration_fence_uneligible', 'true', {
            expires: 2,
            path: '/'
          })
        })
        .catch(error => {
          const httpStatus =
            error.response?.status === 400 ? error.response.status : 'default'
          this.loginFailureSet({ type: 'registration', status: httpStatus })
          manualTrack(this.$refs.registration_button, {
            action_type: 'registration_failed'
          })
        })
    }
  }
}
</script>
